import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Box,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";

import IMG_SOLEIL from "../../resources/RecipesIcons/IMG_SOLEIL.jpg";
import { useTranslation } from "react-i18next";
import VideoPlayer from "../Common/Components/VideoPlayer";
import { useMemo } from "react";

function VideoSection({ recipe, images }) {
  const {
    nomEn,
    nomFr,
    descriptionEn,
    descriptionFr,
    formatVideoEn,
    formatVideoFr,
  } = recipe;

  const [videoLoading, setVideoLoading] = useState(false);
  const [videoExist, setVideoExist] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const { t } = useTranslation(["Recipes"]);
  const abortControllerRef = useRef(null);
  const userState = useSelector((state) => state.user);

  const imageList =
    images && images.length > 0
      ? images.map((image) => `data:image/png;base64,${image}`)
      : [IMG_SOLEIL];

  const imageDescList =
    images && Array.isArray(images) && images.length > 0
      ? images.map((image) =>
          userState.lang === "FR" ? image.textFr : image.textEn
        )
      : "";

  const recetteList = useMemo(
    () =>
      userState.lang === "FR"
        ? descriptionFr.split("\n")
        : descriptionEn.split("\n"),
    [userState.lang, descriptionEn, descriptionFr]
  );

  const fetchVideo = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setVideoLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Video/GetIfVideoExist/${recipe.id}/${userState.lang}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          credentials: "include",
          signal: signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setVideoExist(data);
      if (data) {
        setIsPlaying(true);
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("Error fetching videos:", error);
      }
    } finally {
      setVideoLoading(false);
    }
  };

  useEffect(() => {
    // Check if video exists on component mount
    const checkVideo = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Video/GetIfVideoExist/${recipe.id}/${userState.lang}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
            credentials: "include",
          }
        );

        if (response.ok) {
          const data = await response.json();
          setVideoExist(data);
        }
      } catch (error) {
        console.error("Error checking video:", error);
      }
    };

    checkVideo();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [recipe.id, userState.lang, userState.token]);

  return (
    <Box>
      <Box className="RecipeImageColumn">
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Card className="RecipeMediaContainer">
              {videoLoading ? (
                <CircularProgress
                  className="RecipeLoadingSpinner"
                  size="5rem"
                />
              ) : !isPlaying ? (
                <>
                  {videoExist && (
                    <div
                      className="RecipeVideoPlayButton"
                      onClick={fetchVideo}
                    />
                  )}
                  <CardMedia
                    className="RecipeImage"
                    component="img"
                    image={imageList[0]}
                    alt={nomEn}
                  />
                </>
              ) : (
                <CardContent>
                  <VideoPlayer videoId={recipe.id} language={userState.lang} />
                </CardContent>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box className="RecipeMethodColumn">
        <Typography variant="h4" component="h1" gutterBottom>
          {userState.lang === "FR" ? nomFr : nomEn}
        </Typography>

        {imageList && imageList.length > 1 && (
          <Box mt={2}>
            {imageList.map(
              (image, index) =>
                index > 0 && (
                  <React.Fragment key={index}>
                    <Card className="RecipeExtraImage">
                      <CardMedia component="img" image={image} />
                    </Card>
                  </React.Fragment>
                )
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default VideoSection;
